import React from 'react';
import PageHero from '../components/shared/PageHero';
import CtaBlock from '../components/shared/CtaBlock';
import OfferList from '../components/shared/OfferList';
import { ShieldCheck, DollarSign, AlertTriangle, TrendingDown, Clock, Search, Lock, Eye, CreditCard } from 'lucide-react';

const benefits = [
  { icon: DollarSign, title: 'Prevent Financial Loss', desc: 'Identity theft victims lose $1,100 on average per incident. Compare protection plans with insurance coverage to stay ahead.' },
  { icon: Clock, title: 'Save Time & Stress', desc: 'Resolving identity theft takes 200+ hours. Compare plans with dedicated recovery specialists that do the work for you.' },
  { icon: TrendingDown, title: 'Protect Your Credit', desc: 'ID theft can hit your credit score by 100+ points. Early detection means faster recovery and less damage.' }
];

export default function ProtectCredit() {
  return (
    <>
      <PageHero
        eyebrow="Compare Credit Protection Plans"
        title={<>Compare Top <span className="text-emerald-600">Credit & Identity</span> Protection</>}
        subtitle="Side-by-side reviews of the leading identity monitoring and credit protection plans – ranked by our editors."
        ctaLabel="View All Plans"
        ctaTo="/protect-credit#offers"
        badges={[
          { title: 'Up to $1M', desc: 'Identity Insurance' },
          { title: 'All 3 Bureaus', desc: 'Credit Monitoring' },
          { title: 'Real-Time', desc: 'Fraud Alerts' }
        ]}
      />

      {/* Why essential */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-slate-900 font-bold">Why Compare Protection Plans</h2>
            <p className="mt-4 text-slate-600">Not all identity-protection products are equal. Coverage, insurance, and monitoring depth vary widely – compare before you buy.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="rounded-2xl border border-slate-200 bg-white p-7 hover:border-emerald-300 hover:shadow-lg transition-all group">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center group-hover:bg-emerald-600 transition-colors">
                  <b.icon className="w-6 h-6 text-emerald-700 group-hover:text-white transition-colors" />
                </div>
                <h3 className="mt-5 text-lg font-bold text-slate-900">{b.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Threat awareness */}
      <section className="py-20 lg:py-24 bg-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-25" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/20 border border-red-400/30 text-xs font-semibold text-red-300 uppercase tracking-wider mb-4">
              <AlertTriangle className="w-3.5 h-3.5" /> Threat Awareness
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-white font-bold">Your Data Could Be At Risk</h2>
            <p className="mt-4 text-slate-400 text-sm">Visualization only. No personal data is used.</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-4 mb-10">
            {[
              { icon: Eye, label: 'PERSONAL', text: 'name="John S." email="j***@ex.com" phone="555-***-4567"' },
              { icon: Lock, label: 'ACCOUNTS', text: 'username="jsmith80" password="********" ip="192.168.1.1"' },
              { icon: CreditCard, label: 'FINANCIAL', text: 'account="checking-****" routing="0210*" balance="$4,250"' }
            ].map((c) => (
              <div key={c.label} className="rounded-xl bg-white/5 border border-white/10 p-5 hover:border-red-400/40 transition-colors">
                <div className="flex items-center gap-2 text-red-300 text-xs font-bold uppercase tracking-widest"><c.icon className="w-3.5 h-3.5" /> {c.label}</div>
                <div className="mt-3 font-mono text-xs text-slate-300 break-all">{c.text}</div>
                <div className="mt-3 text-[10px] text-red-400 font-semibold uppercase tracking-widest">⚠ BREACH SIMULATION</div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
              <h4 className="text-white font-bold text-lg mb-3">Common Data Breach Sources</h4>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex gap-2"><Search className="w-4 h-4 text-red-400 mt-0.5" /> Corporate data breaches (retailers, banks, healthcare)</li>
                <li className="flex gap-2"><Search className="w-4 h-4 text-red-400 mt-0.5" /> Phishing and social engineering attacks</li>
                <li className="flex gap-2"><Search className="w-4 h-4 text-red-400 mt-0.5" /> Unsecured public WiFi networks</li>
                <li className="flex gap-2"><Search className="w-4 h-4 text-red-400 mt-0.5" /> Malware and ransomware attacks</li>
              </ul>
            </div>
            <div className="rounded-2xl bg-emerald-500/10 border border-emerald-400/30 p-6">
              <h4 className="text-white font-bold text-lg mb-3">How Identity Protection Helps</h4>
              <ul className="space-y-2 text-sm text-emerald-100">
                <li className="flex gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5" /> Monitors dark web for your personal information</li>
                <li className="flex gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5" /> Alerts you to suspicious credit activity</li>
                <li className="flex gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5" /> Recovery assistance if identity is stolen</li>
                <li className="flex gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5" /> Insurance coverage for financial losses</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Offer list – display ads */}
      <section id="offers" className="py-20 lg:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-4">Editor-Ranked</div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-slate-900 font-bold">Compare Top Protection Plans</h2>
            <p className="mt-4 text-slate-600">Click any banner or button to visit the provider. All offers are updated regularly by our editorial team.</p>
          </div>
          <OfferList category="protection" empty="No protection offers available yet. Check back soon." />
        </div>
      </section>

      <CtaBlock title="Secure Your Identity Today" subtitle="Compare industry-leading protection plans and lock in exclusive Alliance discounts." ctaLabel="Compare Plans" ctaTo="/protect-credit#offers" />
    </>
  );
}
